<template>
<standard-dialog v-model="visible" :width="1280" scrollable persistent>

  <template slot="title">
    <span class="title">{{ title }}</span>
  </template>

  <chart-line v-if="isLineChart" :data="dataChart" :options="options"></chart-line>
  <chart-bar v-if="isBarChart" :data="dataChart" :options="options"></chart-bar>
  <template slot="actions">
    <v-btn class="grey--text" flat @click="doTemperatureConvert" v-if="isTemperatureUnit">
      {{ $t('schedule.enabled') }} {{ userPreferUnitBtnTitle }}
    </v-btn>
    <v-spacer></v-spacer>
    <v-btn color="primary" flat @click="visible=false">{{ $t('dialog.close') }}</v-btn>
  </template>
</standard-dialog>
</template>

<script>
import UserAuth from '@/components/auth/UserAuth'
import Dialog from '@/components/utils/Dialog'
import colors from 'vuetify/es5/util/colors'
import ChartLine from '@/components/charts/Line'
import ChartBar from '@/components/charts/Bar'
import DeviceUnits from './DeviceUnits'
import DeviceLabels from './DeviceLabels'

export default {
  name: 'DeviceChartDialog',
  mixins: [UserAuth],
  props: {
    value: {
      type: Boolean,
      default: false
    },
    itemRaw: {
      type: Object,
      required: true
    }
  },
  components: {
    'standard-dialog': Dialog,
    'chart-line': ChartLine,
    'chart-bar': ChartBar
  },
  data() {
    return {
      rawData: null,
      userPreferUnit: parseInt(this.$cookie.get('userPreferUnit')) || 1
    }
  },
  computed: {
    visible: {
      get() {
        return this.value;
      },
      set(val) {
        this.$emit('input', val);
      }
    },
    title() {
      return [this.item.room, this.item.targetName || this.item.name].filter(s => s).join(' ');
    },
    dataJSON() {
      return JSON.parse((this.rawData || {}).result || '[]');
    },
    dataTrimBasicValue() {
      return this.dataJSON.map(d => {
        if (d[2] > 100) d[2] = 100;
        return d;
      });
    },
    item() {
      return this.itemRaw
    },
    dataItems() {
      if (this.isSensorValueUsed) {
        return this.dataJSON.map(k => ({
          t: k[0],
          y: this.realTemperatureConvert(k[1] / this.valueGain)
        }));
      } else {
        return this.dataTrimBasicValue.map(k => ({
          t: k[0],
          y: k[2]
        }));
      }
    },
    dataChart() {
      return {
        datasets: [{
          data: this.dataItems,
          borderWidth: 1.5,
          steppedLine: this.isLineStepped,
          lineTension: 0,
          pointRadius: 0,
          borderColor: colors.blue.base,
          backgroundColor: colors.blue.base,
          fill: false
        }]
      };
    },
    options() {
      const ctx = this;
      return {
        legend: {
          display: false
        },
        scales: {
          xAxes: [{
            type: 'time',
            time: {
              unit: this.xAxisUnit,
              max: this.xAxisMaximum,
              min: this.xAxisMinimum
            },
            stacked: this.isBarStacked
          }],
          yAxes: [{
            ticks: {
              beginAtZero: this.unitMinimum == 0,
              suggestedMin: this.unitMinimum,
              suggestedMax: this.unitMaximum,
              callback: function(value, index, values) {
                let labels = DeviceLabels[ctx.item.functype]
                if (labels) {
                  let l = labels[value];
                  if (l)
                    return ctx.$t(l);
                  else
                    return null;
                }

                if (value < 10000)
                  return value;
                else
                  return (value / 1000).toFixed(0) + 'k'
              }
            },
            scaleLabel: {
              display: !!this.unitLabel,
              labelString: this.unitLabel
            }
          }]
        },
        tooltips: {
          intersect: false,
          mode: 'nearest',
          callbacks: {
            beforeBody(items) {
              (items || []).forEach(item => {
                item.value = Math.round(item.value * 100) / 100;
              });
            }
          }
        },
        responsive: true,
        maintainAspectRatio: false,
        animation: false
      };
    },
    xAxisUnit() {
      if (this.isMeter) return null;
      return 'day';
    },
    xAxisMaximum() {
      return new Date().getTime();
    },
    xAxisMinimum() {
      return this.xAxisMaximum - this.juiceTimeRange * 24 * 60 * 60 * 1000;
    },
    unitPrefer() {
      if (this.isMeter) return 5;
      if (this.isPercentage) return 8;
      return this.item.unit;
    },
    unitLabel() {
      if (this.isBarChart) return this.$t('kanban.triggers');
      if (this.unit) return this.dataUnitName(this.unit);
    },
    unitMinimum() {
      if (this.unit) return DeviceUnits.minimums[this.unit] || 0;
    },
    unitMaximum() {
      if (this.unit) return DeviceUnits.maximums[this.unit] || 0;
    },
    unit() {
      let unit = this.unitPrefer;
      if (this.isTemperatureUnit) {
        unit = this.userPreferUnit;
      };
      return unit
    },
    isSensorValueUsed() {
      return [11, 12, 14, 15, 18, 19, 22, 29, 32, 33, 34, 37, 40].includes(this.item.functype);
    },
    isTemperatureUnit() {
      return this.item.unit === 1 || this.item.unit === 2
    },
    isLineChart() {
      return !this.isBarChart;
    },
    isLineStepped() {
      return !this.isSensorValueUsed || [32, 33].includes(this.item.functype);
    },
    isBarChart() {
      return [14].includes(this.item.functype);
    },
    isBarStacked() {
      return this.isBarChart;
    },
    isPercentage() {
      return [24, 26, 30, 41].includes(this.item.functype);
    },
    valueGain() {
      if (this.unitPrefer && this.isSensorValueUsed) {
        let gain = DeviceUnits.factors[this.unitPrefer];
        if (gain) return gain;
      }
      return 1;
    },
    userPreferUnitBtnTitle() {
      return this.userPreferUnit === 1 ? this.dataUnitName(2): this.dataUnitName(1)
    },
    isMeter() {
      return [22, 29].includes(this.item.functype);
    },
    juiceCommand() {
      if (this.isBarChart) return 'histogram';
      return 'trend';
    },
    juiceTimeRange() {
      return this.isMeter ? 1 : 7;
    },
    juiceEventCodes() {
      return {
        11: 4801,
        12: 4802,
        13: [4102, 4103],
        14: 4101,
        15: 4803,
        20: [4107, 4119],
        22: 4804,
        23: [5002, 5102],
        24: [5002, 5102],
        26: 4804,
        29: 4804,
        30: 5002,
        31: 5002,
        34: 4805,
        36: [5002, 5102],
        41: [5002, 5102]
      }
    }
  },
  methods: {
    load() {
      let ref = this.$db.ref('macJuice')
        .child(this.item.mac)
        .child(`${this.item.key}-${this.juiceCommand}`);
      this.$bindAsObject('rawData', ref, null, function() {
        let now = new Date().getTime();
        let t = ((this.rawData || {}).timestamp || [])[0] || 0;
        if (now - t > 5 * 60 * 1000) this.refresh();
      });
    },
    refresh() {
      let [mac, uid, cid] = this.item.key.split('-');
      this.$db.ref('command/juice').push().set({
        deviceChart: true,
        user: this.userId,
        mac: mac,
        uid: uid,
        cid: cid,
        eventCode: this.juiceEventCodes[this.item.functype],
        juice: this.juiceCommand,
        days: this.juiceTimeRange
      });
    },
    dataUnitName(val) {
      return DeviceUnits.names[val]
    },
    doTemperatureConvert() {
      if (this.userPreferUnit === 1) {
        this.userPreferUnit = 2;
      } else {
        this.userPreferUnit = 1;
      }
      this.$cookie.set('userPreferUnit', this.userPreferUnit);
    },
    realTemperatureConvert(val) {
      if (this.isTemperatureUnit) {
        if (this.userPreferUnit === this.unitPrefer) {
          return val
        } else {
          if (this.userPreferUnit === 1) {
            return ((val - 32) / 1.8).toFixed(0);
          } else {
            return (val * 1.8 + 32).toFixed(0);
          }
        }
      } else {
        return val
      }
    }
  },
  mounted() {
    this.load();
  }
}
</script>



// WEBPACK FOOTER //
// src/components/device/DeviceChartDialog.vue